import { useCallback, useContext, useEffect, useRef, useState } from "react";
import apiManutencao from "../../services/apis/apiManutencao";
import useToast from "../../hooks/useToast";
import { AuthContext } from "../../contexts/AuthContext";
import { getErrorMessage } from "../../utils/errorMessage";
import TarefasDoDia from "../../components/manutencao/TarefasDoDia";
import ListaTabelasManutencao from "../../components/manutencao/ListaTabelasManutencao";
import RelatorioManutencao from "../../components/manutencao/RelatorioManutencao";
import { executarAcaoTarefa, mensagemErroAcao } from "../../components/manutencao/acoesTarefa";

export default function Manutencao() {
    const { userInfo } = useContext(AuthContext);
    const podeEditar = userInfo?.cargo && userInfo.cargo !== 'BASIC_USER';
    const [tabelas, setTabelas] = useState([]);
    const [tarefas, setTarefas] = useState([]);
    const [carregandoTabelas, setCarregandoTabelas] = useState(true);
    const [carregandoTarefas, setCarregandoTarefas] = useState(true);
    const [erroTabelas, setErroTabelas] = useState(false);
    const [erroTarefas, setErroTarefas] = useState(false);
    const requisicaoTarefasRef = useRef(0);
    const { sucesso, erro } = useToast();

    const carregarTabelas = useCallback(async () => {
        setCarregandoTabelas(true);
        try {
            const res = await apiManutencao.getTabelas();
            setTabelas(res.data);
            setErroTabelas(false);
        } catch (error) {
            setErroTabelas(true);
            erro(getErrorMessage(error, 'Erro ao carregar tabelas de manutenção'));
        } finally {
            setCarregandoTabelas(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const carregarTarefas = useCallback(async () => {
        const requisicao = ++requisicaoTarefasRef.current;
        setCarregandoTarefas(true);
        try {
            const res = await apiManutencao.getTarefasDoDia();
            if (requisicao !== requisicaoTarefasRef.current) return;
            setTarefas(res.data);
            setErroTarefas(false);
        } catch (error) {
            if (requisicao !== requisicaoTarefasRef.current) return;
            setErroTarefas(true);
            erro(getErrorMessage(error, 'Erro ao carregar tarefas do dia'));
        } finally {
            if (requisicao === requisicaoTarefasRef.current) setCarregandoTarefas(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        carregarTabelas();
        carregarTarefas();
    }, [carregarTabelas, carregarTarefas]);

    const executar = async (acao, mensagemSucesso, mensagemErro, recarregarTarefas = true) => {
        try {
            await acao();
            sucesso(mensagemSucesso);
            await carregarTabelas();
            if (recarregarTarefas) carregarTarefas();
            return true;
        } catch (error) {
            erro(getErrorMessage(error, mensagemErro));
            return false;
        }
    };

    const criarTabela = (dados) => executar(
        () => apiManutencao.postTabela(dados),
        'Tabela criada com sucesso!', 'Erro ao criar tabela de manutenção', false);

    const editarTabela = (id, dados) => executar(
        () => apiManutencao.putTabela(id, dados),
        'Tabela atualizada com sucesso!', 'Erro ao atualizar tabela de manutenção');

    const excluirTabela = (id) => executar(
        () => apiManutencao.deleteTabela(id),
        'Tabela excluída com sucesso!', 'Erro ao excluir tabela de manutenção');

    const carregarModeloPadrao = () => executar(
        () => apiManutencao.carregarModeloPadrao(),
        'Modelo padrão carregado com sucesso!', 'Erro ao carregar modelo padrão');

    const criarItem = (item) => executar(
        () => apiManutencao.postItem(item),
        'Ação adicionada com sucesso!', 'Erro ao adicionar ação');

    const editarItem = (id, item) => executar(
        () => apiManutencao.putItem(id, item),
        'Ação atualizada com sucesso!', 'Erro ao atualizar ação');

    const excluirItem = (id) => executar(
        () => apiManutencao.deleteItem(id),
        'Ação excluída com sucesso!', 'Erro ao excluir ação');

    const alternarTarefa = async (tarefa, acao, dados) => {
        try {
            await executarAcaoTarefa(tarefa.id, acao, dados);
            await carregarTarefas();
            return true;
        } catch (error) {
            erro(getErrorMessage(error, mensagemErroAcao(acao)));
            carregarTarefas();
            return false;
        }
    };

    return (
        <div className="pb-[100px]">
            <div className="flex flex-wrap items-center justify-between gap-2 p-2">
                <h1 className="text-white text-3xl font-semibold">Manutenção</h1>
                <RelatorioManutencao />
            </div>
            <TarefasDoDia
                tarefas={tarefas}
                carregando={carregandoTarefas}
                erroCarregamento={erroTarefas}
                onTentarNovamente={carregarTarefas}
                onAlternar={alternarTarefa}
            />
            <ListaTabelasManutencao
                tabelas={tabelas}
                carregando={carregandoTabelas}
                erroCarregamento={erroTabelas}
                podeEditar={podeEditar}
                onTentarNovamente={carregarTabelas}
                onCriarTabela={criarTabela}
                onEditarTabela={editarTabela}
                onExcluirTabela={excluirTabela}
                onCarregarModeloPadrao={carregarModeloPadrao}
                onCriarItem={criarItem}
                onEditarItem={editarItem}
                onExcluirItem={excluirItem}
            />
        </div>
    );
}
